import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  AlertTriangle, 
  ArrowLeft, 
  Home, 
  Search, 
  Plus,
  MessageSquare,
  Clock
} from "lucide-react";

interface PositDeletedProps {
  /** The type of page the user was trying to access */
  context?: 'chat' | 'edit' | 'view' | 'embed';
  /** Whether the user is authenticated */
  isAuthenticated?: boolean;
  /** Additional message to show */
  message?: string;
  /** The posit ID that was being accessed */
  positId?: string;
}

const PositDeleted: React.FC<PositDeletedProps> = ({
  context = 'view',
  isAuthenticated = false,
  message,
  positId
}) => {
  const getContextualTitle = () => {
    switch (context) {
      case 'chat':
        return 'Chat Not Available';
      case 'edit':
        return 'Edit Not Available';
      case 'embed':
        return 'Embed Not Available';
      default:
        return 'Posit Not Available';
    }
  };

  const getContextualDescription = () => {
    switch (context) {
      case 'chat':
        return 'The conversation you\'re looking for is no longer available. The posit may have been deleted by its creator.';
      case 'edit':
        return 'The posit you\'re trying to edit is no longer available. It may have been deleted or you may not have permission to edit it.';
      case 'embed':
        return 'The embedded posit is no longer available. It may have been deleted or embedding may have been disabled.';
      default:
        return 'The posit you\'re looking for is no longer available. It may have been deleted or made private.';
    }
  };

  const getSuggestedActions = () => {
    const actions = [];

    if (isAuthenticated) {
      actions.push({
        icon: Plus,
        title: 'Create New Posit',
        description: 'Start building your own knowledge base',
        link: '/create-posit',
        variant: 'default' as const
      });
      
      actions.push({
        icon: MessageSquare,
        title: 'Explore Posits',
        description: 'View and manage your existing Posits',
        link: '/posits',
        variant: 'outline' as const
      });
    }

    actions.push({
      icon: Search,
      title: 'Explore Public Posits',
      description: 'Discover knowledge shared by other creators',
      link: '/p/explore',
      variant: 'outline' as const
    });

    actions.push({
      icon: Home,
      title: 'Go to Homepage',
      description: 'Start fresh from the beginning',
      link: '/',
      variant: 'outline' as const
    });

    return actions;
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="flex flex-col items-center justify-center gap-8 text-center min-h-[60vh]">
        {/* Main Alert */}
        <Alert className="max-w-2xl">
          <AlertTriangle className="h-5 w-5" />
          <AlertDescription className="text-left">
            <div className="font-semibold mb-2">{getContextualTitle()}</div>
            <div className="text-sm text-muted-foreground">
              {message || getContextualDescription()}
            </div>
          </AlertDescription>
        </Alert>

        {/* Contextual Information */}
        <div className="max-w-2xl">
          <div className="grid gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 justify-center">
              <Clock className="h-4 w-4" />
              <span>This might happen if the posit was recently deleted or made private</span>
            </div>
            {positId && (
              <div className="text-xs font-mono bg-muted px-2 py-1 rounded">
                Posit ID: {positId}
              </div>
            )}
          </div>
        </div>

        {/* Suggested Actions */}
        <div className="w-full max-w-4xl">
          <h3 className="text-lg font-semibold mb-6">What would you like to do next?</h3>
          
          <div className="grid gap-4 md:grid-cols-2">
            {getSuggestedActions().map((action, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <action.icon className="h-5 w-5" />
                    {action.title}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {action.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button 
                    asChild 
                    variant={action.variant}
                    className="w-full"
                  >
                    <Link to={action.link}>
                      {action.title}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Back Navigation */}
        <div className="mt-8">
          <Button 
            variant="ghost" 
            className="flex items-center gap-2" 
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PositDeleted; 